
import { Vue, Component } from "vue-property-decorator";
import IconButton from "@/components/UI/IconButton.vue";
import Dropdown from "@/components/Reusable/Dropdown/Dropdown.vue";
import { exportGlobalSettings } from "@/lib/global-settings/import-export";
import Dialog from "@/components/UI/Dialog.vue";
import FileUpload from "@/components/Reusable/CustomInputs/FileUpload.vue";
import NotificationLogger from "@/lib/notifications/notifications";
import { pipe } from "fp-ts/lib/function";
import { globalStore } from "@/store/store";
import { PropertiesData } from "@/types/tree";
import fpJsonParse from "@/util/fp-json-parse";
import { fold } from "fp-ts/lib/Either";
import * as t from "io-ts";
import { breakdanceUndo } from "@/lib/undo/undo";

@Component({
  components: {
    IconButton,
    Dropdown,
    Dialog,
    FileUpload
  }
})
export default class GlobalSettingsDropdownMenu extends Vue {
  exportGlobalSettings() {
    exportGlobalSettings();
  }

  canImport = false;

  openImportDialog() {
    this.importDialogOpen = true;
  }

  importDialogOpen = false;

  cancelImport() {
    this.settingsToImport = {};
    this.canImport = false;
    this.importDialogOpen = false;
  }

  settingsToImport = {};

  handleFiles(files: FileList) {
    Array.from(files).forEach((file: File) => {
      const fileReader = new FileReader();
      fileReader.onloadend = async e => {
        if (!e?.target) return;

        const text = e.target.result as string;

        const parsedJson = pipe(
          fpJsonParse(text),
          fold(
            () => {},
            parsed => parsed as PropertiesData
          )
        );

        if (!("settings" in parsedJson)) {
          NotificationLogger.log.errorMessage(
            `Error importing Global Settings - the file is invalid or empty.`
          );
          this.settingsToImport = {};
          this.canImport = false;
          return;
        }

        pipe(
          parsedJson,
          t.type({
            settings: t.record(t.string, t.unknown)
          }).decode,
          fold(
            () => {
              NotificationLogger.log.errorMessage(
                `Error importing Global Settings - the file is invalid.`
              );
              this.settingsToImport = {};
              this.canImport = false;
            },
            parsed => {
              this.settingsToImport = parsed;
              this.canImport = true;
            }
          )
        );
      };
      fileReader.readAsText(file);
    });
  }

  importGlobalSettings() {
    if (!("settings" in this.settingsToImport)) {
      NotificationLogger.log.errorMessage(`No settings to import.`);
      return;
    }

    breakdanceUndo.transaction(() => {
      globalStore.setGlobalSettings(this.settingsToImport);
    }, `Import Global Settings`);

    NotificationLogger.log.message("Global Settings imported.");

    this.settingsToImport = {};
    this.canImport = false;
    this.importDialogOpen = false;
  }
}
